import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';

import DateService from 'src/core/services/DateService';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import {
  GetTaxationsResponse,
  GetTaxationsVariables,
  IBaseDrawerProps,
  IExportTaxationFilters
} from '@common/types';
import {
  ALL,
  exportTaxationDrawerSections,
  exportTaxationExcelColumnWidths,
  exportTaxationExcelHeaderGroups,
  exportTaxationExcelHeaders,
  exportTaxationExcelTemplateStyles
} from '@common/constants';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';
import { formatForFileName, normalizeMulti } from './exportReinsurers.drawer';
import { IAbstractRecord } from '@models';
import { useGetLines, useGetTaxations } from '@common/slices';

export const buildLovs = (lovData: any) => {
  const lineslist = lovData?.planConfig?.getLines.items || [];
  const Lines: Record<string, string> = { [ALL]: 'All' };

  lineslist.forEach((n: any) => {
    if (n?.code) Lines[String(n.id)] = String(`${n.code} - ${n.name}`);
  });

  return { Lines };
};

export const fetchAllTaxations = async (
  getTaxations: (options?: { variables: GetTaxationsVariables }) => Promise<{ data: GetTaxationsResponse }>,
  vars: {
    lines: string[] | null;
    effectiveDateFrom: any | null;
    effectiveDateTo: any | null;
    keywordSearch: string | null;
    OrderBy: any | null;
  }
) => {
  const pageSize = 500;
  let pageNumber = 1;

  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await getTaxations({
      variables: {
        pagination: { pageSize, pageNumber },
        lines: vars.lines,
        effectiveDateFrom: vars.effectiveDateFrom,
        effectiveDateTo: vars.effectiveDateTo,
        keyword: vars.keywordSearch,
        orderBy: vars.OrderBy
      }
    });

    const items = data?.core?.searchTaxations?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    pageNumber += 1;
  }

  return all;
};

export const mapItemsToExcelData = (data: IAbstractRecord[]) => {
  const arr: any[] = [];

  (data ?? []).forEach((p: any) => {
    const base = {
      line: `${p.line.code}-${p.line.name}`,
      code: p.code,
      effectiveFrom: p.effectiveFrom,
      effectiveTo: p.effectiveTo,
      isLatest: p.isLatest ? 'Yes' : 'No',
      fixedStampCurrency: p.fixedStampCurrencyValue?.title ?? '',
      fixedStamp: p.fixedStamp,
      proportionalStamp: p.proportionalStamp,
      municipalityStamp: p.municipalityStamp,
      taxOnCommission: p.taxOnCommission,
      reinsuranceTax: p.reinsuranceTax,
      applicableToAllPlans: p.applicableToAllPlans ? 'Yes' : 'No'
    };

    const tpsList = p.taxationPlansSpecific ?? [];

    if (!tpsList.length) {
      arr.push({
        ...base,
        plan: '',
        fixedStampCurrencyPlan: '',
        fixedStampPlan: '',
        proportionalStampPlan: '',
        municipalityStampPlan: '',
        taxOnCommissionPlan: '',
        reinsuranceTaxPlan: ''
      });
      return;
    }

    tpsList.forEach((tps: any) => {
      arr.push({
        ...base,
        plan: `${tps?.plan?.code}-${tps?.plan?.name}`,
        fixedStampCurrencyPlan: tps.fixedStampCurrencyValue?.title ?? '',
        fixedStampPlan: tps.fixedStamp,
        proportionalStampPlan: tps.proportionalStamp,
        municipalityTaxPlan: tps.municipalityTax,
        taxOnCommissionPlan: tps.taxOnCommission,
        reinsuranceTaxPlan: tps.reinsuranceTax
      });
    });
  });

  return arr.sort((a: any, b: any) =>
    String(a.code || '').localeCompare(String(b.code || ''))
  );
};


const ExportTaxationDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [getTaxations] = useGetTaxations.useLazy();
  const [values, setValues] = useState<IExportTaxationFilters>({
    taxationLine: [ALL],
    effectiveDate: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);
  const { data: lovData } = useGetLines.useQuery();

  useEffect(() => {
    setLovs({});
    setSections([]);
    setValues({ taxationLine: [ALL], effectiveDate: [] });

    if (!lovData) return;

    const built = buildLovs(lovData);
    setLovs(built);
    setSections(exportTaxationDrawerSections(values, built));
  }, [lovData]);

  const submitForm = async (formValues: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

    try {
      const selectedLines = normalizeMulti(data.taxationLine);

      const formattedDateRange = DateService.formatDateTimeRangeBackend(
        data.effectiveDate
      );

      const effectiveDateFrom = formattedDateRange?.[0] || null;
      const effectiveDateTo = formattedDateRange?.[1] || null;

      const linesVar = selectedLines.includes(ALL) ? [] : selectedLines;

      const pickerFrom = data.effectiveDate?.[0];
      const pickerTo = data.effectiveDate?.[1];

      let fileName = 'Redbridge_Taxation';
      if (pickerFrom && pickerTo) {
        fileName += `_${formatForFileName(pickerFrom)}_${formatForFileName(
          pickerTo
        )}`;
      }

      const items = await fetchAllTaxations(getTaxations, {
        lines: linesVar,
        effectiveDateFrom,
        effectiveDateTo,
        keywordSearch: '',
        OrderBy: null
      });

      if (!items.length) {
        toast.error(
          <ToastErrorMessage>
            No taxations found for the selected filters
          </ToastErrorMessage>
        );

        return;
      }

      const excelData = mapItemsToExcelData(items);

      downloadExcelFile({
        data: excelData,
        filename: fileName,
        headers: exportTaxationExcelHeaders,
        sheetName: 'Sheet2',
        groupHeaders: exportTaxationExcelHeaderGroups,
        styles: exportTaxationExcelTemplateStyles,
        columnWidths: exportTaxationExcelColumnWidths,
        freezeHeaderRows: 0
      });

      onSuccess();
    } catch (e) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      console.log('test');
    }
  };

  const handleChange = (
    fieldName: keyof IExportTaxationFilters,
    value: any,
    sectionIndex: number,
    updatedValues: IExportTaxationFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(exportTaxationDrawerSections(newValues, lovs));
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      onChange={handleChange}
    />
  );
};

export { ExportTaxationDrawer };
